import {
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  LinkOutlined,
  PaperClipOutlined,
  PlusOutlined,
  WarningFilled
} from "@ant-design/icons"
import { App, Button, DatePicker, Flex, Form, InputNumber, Tooltip } from "antd"
import { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import {
  type CellEditors,
  buildEditRowCells,
  isRowCancelEscape,
  rowCommitKeyDown
} from "../../../../../../../components/EntityTable/editable-row-cells.ts"
import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { FormChangedWatcher } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import { empty } from "../../../../../../../constants/placeholders"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import type { RootState } from "../../../../../../../store.ts"
import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import type { ProjectMilestoneFilterOptions } from "../../../../../redux/project.types.ts"
import type { ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import { MILESTONE_STATUSES } from "../../../../constants/milestone-statuses.ts"
import { completedActualEndDefault } from "../../../../utils/milestone-completed-actual-end.ts"
import {
  accountToday,
  isFinishedLate,
  milestoneVarianceDays
} from "../../../../utils/milestone-schedule-state.ts"
import type { EffectiveScheduleEntry } from "../../../../utils/milestone-effective-schedule.ts"
import { milestoneEffortRules } from "../../../../utils/milestone-effort.ts"
import {
  type MilestoneSaveResult,
  useMilestoneScheduleFields
} from "./use-milestone-schedule-fields.ts"
import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"
import { PlannedMoneyInput } from "../../../../../components/PlannedMoneyInput.tsx"
import { MILESTONE_EDITABLE_COLUMN_KEYS, ORDER_COLUMN_KEY } from "./columns.tsx"
import { MilestoneRiskTag } from "./MilestoneRiskTag.tsx"
import { TextArea } from "../../../../../../../components/Input/TextArea.tsx"
import { renderMilestoneStatusOption } from "./MilestoneStatusTag.tsx"

// The form fields each editable column's editor writes.
const COLUMN_FIELDS: Partial<
  Record<
    (typeof MILESTONE_EDITABLE_COLUMN_KEYS)[number],
    (keyof ProjectMilestone)[]
  >
> = {
  name: ["name"],
  milestone_category: ["milestone_category"],
  status: ["status"],
  description: ["description"],
  receivables: ["planned_receivable"],
  payables: ["planned_payable"],
  start_date: ["start_date"],
  end_date: ["end_date"],
  duration_days: ["duration_days"],
  actual_end_date: ["actual_end_date"]
}

interface ProjectViewMilestoneNewRowProps {
  record: ProjectMilestone
  children?: React.ReactNode[]
  options: ProjectMilestoneFilterOptions
  mode?: "create" | "edit"
  /** When this milestone's dates are currently shifted by a dependency, the
   * edit form must open on those same calculated dates — not the raw stored
   * ones the read-only row no longer shows — or the two views visibly
   * disagree the moment you open the row. */
  effectiveEntry?: EffectiveScheduleEntry
  onSelectRow: (
    record: ProjectMilestone,
    action: string
  ) => Promise<MilestoneSaveResult | void> | MilestoneSaveResult | void
}

export const ProjectViewMilestoneNewRow = ({
  record,
  onSelectRow,
  options,
  mode = "create",
  effectiveEntry,
  ...rest
}: ProjectViewMilestoneNewRowProps) => {
  // The cells the table rendered for this row. With its data columns among
  // them, each editor replaces its own column's cell, so the row follows the
  // saved order and hidden columns. Without them (a caller passing only the
  // leading cells) the row ends the table's cells at # and lays out every
  // editor in the default order.
  const tableCells = useMemo(() => {
    const cells: React.ReactNode[] = []
    React.Children.forEach(rest?.children, (child) => cells.push(child))
    return cells
  }, [rest?.children])
  const cellKeys = useMemo(
    () =>
      new Set(
        tableCells.flatMap((cell) =>
          React.isValidElement(cell) && cell.key != null
            ? [String(cell.key)]
            : []
        )
      ),
    [tableCells]
  )
  const byColumn = MILESTONE_EDITABLE_COLUMN_KEYS.some((key) =>
    cellKeys.has(key)
  )
  const leadingCells = useMemo(() => {
    const orderAt = tableCells.findIndex(
      (cell) =>
        React.isValidElement(cell) && String(cell.key) === ORDER_COLUMN_KEY
    )
    return orderAt < 0 ? [] : tableCells.slice(0, orderAt + 1)
  }, [tableCells])

  const { t } = useTranslation()
  const {
    getDateValueProps,
    createDateRangeRules,
    normalizeDate,
    parseDate,
    userFormat
  } = useDateFormat()
  const accountTimezone = useSelector(
    (state: RootState) => state.auth.userInfo?.timezone
  )
  // Typed dates parse in the account's format first, ISO second; without an
  // explicit format antd only ever parses what the picker locale says.
  const pickerFormat = userFormat ? [userFormat, DATE_FORMAT] : [DATE_FORMAT]
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<ProjectMilestone>()
  const { modal } = App.useApp()
  const schedule = useMilestoneScheduleFields(form, normalizeDate)

  const initialValues = useMemo(
    () =>
      effectiveEntry?.isShifted
        ? {
            ...record,
            start_date: effectiveEntry.start.format(userFormat || DATE_FORMAT),
            end_date: effectiveEntry.end.format(userFormat || DATE_FORMAT)
          }
        : record,
    [record, effectiveEntry, userFormat]
  )

  const watch = Form.useWatch(
    (s) => ({
      status: s?.status,
      actualEnd: s?.actual_end_date,
      plannedEnd: s?.end_date
    }),
    form
  )

  const isCompleted = watch?.status === MILESTONE_STATUSES.COMPLETED
  const completedWithoutActualEnd = isCompleted && !watch?.actualEnd
  const isLate = isFinishedLate(watch?.actualEnd, watch?.plannedEnd, parseDate)
  const variance = milestoneVarianceDays(
    record,
    accountToday(accountTimezone),
    parseDate
  )

  // A column hidden in Table settings renders no editor, so its fields are
  // not in the form's validated values. They are read from the form store,
  // which starts at the row's values and also holds what a visible editor
  // wrote into them (Completed fills Actual End, Actual End completes).
  const hiddenFieldValues = (): Partial<ProjectMilestone> => {
    if (!byColumn) return {}
    const store = form.getFieldsValue(true) as Partial<ProjectMilestone>
    return Object.fromEntries(
      MILESTONE_EDITABLE_COLUMN_KEYS.filter((key) => !cellKeys.has(key))
        .flatMap((key) => COLUMN_FIELDS[key] ?? [])
        .map((field) => [
          field,
          field in store ? store[field] : initialValues[field]
        ])
    )
  }

  const handleSave = async () => {
    try {
      const { id, ...initialData } = record
      const values = {
        ...(await validateFormWithScroll(form)),
        ...hiddenFieldValues()
      }

      setSaving(true)

      const action = mode === "create" ? "create" : "update"
      const result = await onSelectRow(
        {
          ...schedule.withEditedScheduleOnly(values),
          actual_end_date: normalizeDate(values?.actual_end_date)
        } as ProjectMilestone,
        action
      )
      if (result) schedule.showServerErrors(result)
    } catch (error: any) {
      if (!error?.errorFields) throw error
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const action = mode === "create" ? "cancel_create" : "cancel"

    const isChanged = isFormChanged<ProjectMilestone>(
      form.getFieldsValue(),
      initialValues
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          onSelectRow(record, action)
        }
      })
    } else {
      onSelectRow(record, action)
    }
  }

  const onStatusChangeHandler = (value: number) => {
    if (value === MILESTONE_STATUSES.COMPLETED) {
      if (!form.getFieldValue("actual_end_date")) {
        form.setFieldValue(
          "actual_end_date",
          completedActualEndDefault(
            parseDate(form.getFieldValue("end_date")),
            accountTimezone
          )
        )
      }
    }
  }

  // Symmetric to the above: entering when a milestone actually finished
  // normally means it's done, so default the status forward too — the same
  // existing status behavior, not a second one. Only on entering a value
  // (never on clearing it) and only when status isn't already Completed, so
  // this never fights a status the user set on purpose.
  const onActualEndChangeHandler = (value: Dayjs | null) => {
    if (
      value &&
      form.getFieldValue("status") !== MILESTONE_STATUSES.COMPLETED
    ) {
      form.setFieldValue("status", MILESTONE_STATUSES.COMPLETED)
    }
  }

  const rowClassName = mode === "create" ? "bg-blue-50" : ""

  // Escape cancels from the moment the row opens, not only once focus is in
  // it: a double-click to edit leaves focus on nothing. The row's own handler
  // covers keys pressed inside it; an Escape aimed at anything else (a search
  // box, a popover, a dialog) is left to that.
  const rowRef = useRef<HTMLTableRowElement>(null)
  const cancelRef = useRef(handleCancel)
  cancelRef.current = handleCancel
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || saving) return
      if (!isRowCancelEscape(event)) return
      if (rowRef.current?.contains(event.target as Node)) return
      const target = event.target
      if (target !== document.body && target !== document.documentElement)
        return
      const dialogOpen = Array.from(
        document.querySelectorAll<HTMLElement>(".ant-modal-wrap")
      ).some((wrap) => wrap.style.display !== "none")
      if (dialogOpen) return
      event.preventDefault()
      cancelRef.current()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [saving])

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    busy: saving,
    cancel: handleCancel
  })

  // Keyed by column, so the row follows the table's saved order, widths and
  // hidden columns instead of a fixed sequence of cells.
  const editors: CellEditors = {
    // Milestone Name
    name: (
      <td className="ant-table-cell">
        <Form.Item<ProjectMilestone>
          name="name"
          noStyle
          rules={[
            {
              required: true,
              whitespace: true,
              message: t("projectMilestones.form.nameRequired")
            },
            {
              max: 255,
              message: t("projectMilestones.form.nameMax")
            }
          ]}
        >
          <TextArea
            autoSize
            placeholder={t("projectMilestones.inlineRow.namePlaceholder")}
          />
        </Form.Item>
      </td>
    ),
    // Status
    milestone_category: (
      <td className="ant-table-cell">
        <Form.Item<ProjectMilestone> name="milestone_category" noStyle>
          <ResolvedSelect
            className="w-full"
            placeholder={t("projectMilestones.inlineRow.typePlaceholder")}
            options={options?.["category"]}
          />
        </Form.Item>
      </td>
    ),
    // Status
    status: (
      <Tooltip
        title={
          completedWithoutActualEnd
            ? t("projectMilestones.tooltips.completedNeedsActualEnd")
            : ""
        }
        color="orange"
      >
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestone>
            name="status"
            noStyle
            rules={[
              {
                required: true,
                message: t("projectMilestones.form.required")
              }
            ]}
          >
            <ResolvedSelect
              status={completedWithoutActualEnd ? "warning" : ""}
              // Fills the cell rather than setting its own width, so the
              // column's declared width is what decides it.
              className="w-full"
              placeholder={t("projectMilestones.inlineRow.statusPlaceholder")}
              onChange={onStatusChangeHandler}
              options={options?.["status"]}
              optionRender={renderMilestoneStatusOption}
            />
          </Form.Item>
        </td>
      </Tooltip>
    ),
    // Risk
    schedule_risk: (
      <td className="ant-table-cell text-center">
        <MilestoneRiskTag risk={record?.schedule_risk} />
      </td>
    ),
    // Comments
    description: (
      <td className="ant-table-cell">
        <Form.Item<ProjectMilestone>
          name="description"
          noStyle
          rules={[
            {
              whitespace: true,
              max: 250,
              message: t("projectMilestones.inlineRow.descriptionMax")
            }
          ]}
        >
          <TextArea
            rows={2}
            autoSize
            placeholder={t(
              "projectMilestones.inlineRow.descriptionPlaceholder"
            )}
          />
        </Form.Item>
      </td>
    ),
    // Receivables
    receivables: (
      <td className="ant-table-cell text-center">
        <Tooltip title={t("projectMilestones.tooltips.plannedReceivable")}>
          <PlannedMoneyInput name="planned_receivable" className="m-0" />
        </Tooltip>
      </td>
    ),
    // Payables
    payables: (
      <td className="ant-table-cell text-center">
        <Tooltip title={t("projectMilestones.tooltips.plannedPayable")}>
          <PlannedMoneyInput name="planned_payable" className="m-0" />
        </Tooltip>
      </td>
    ),
    // Planned Start
    start_date: (
      <td className="ant-table-cell">
        <Form.Item<ProjectMilestone>
          name="start_date"
          noStyle
          getValueProps={getDateValueProps}
          dependencies={["end_date"]}
          rules={createDateRangeRules<ProjectMilestone>("end_date", "start", {
            start: t("projectMilestones.form.endBeforeStart")
          })}
        >
          <DatePicker
            format={pickerFormat}
            onChange={(value) => schedule.onEdit("start_date", value)}
          />
        </Form.Item>
      </td>
    ),
    // Planned End
    end_date: (
      <td className="ant-table-cell">
        {/* Not noStyle: the end-before-start message shows under the field,
            where a red outline alone left the user guessing. */}
        <Form.Item<ProjectMilestone>
          name="end_date"
          style={{ marginBottom: 0 }}
          getValueProps={getDateValueProps}
          dependencies={["start_date"]}
          rules={createDateRangeRules<ProjectMilestone>("start_date", "end", {
            end: t("projectMilestones.form.endBeforeStart")
          })}
        >
          <DatePicker
            format={pickerFormat}
            onChange={(value) => schedule.onEdit("end_date", value)}
          />
        </Form.Item>
      </td>
    ),
    // Effort
    duration_days: (
      <td className="ant-table-cell">
        <Tooltip title={t("projectMilestones.tooltips.effort")}>
          <Form.Item<ProjectMilestone>
            name="duration_days"
            noStyle
            rules={milestoneEffortRules(t("projectMilestones.form.effortMin"))}
          >
            <InputNumber
              className="w-full"
              precision={0}
              aria-label={t("projectMilestones.form.effort")}
              placeholder={t("projectMilestones.form.effortPlaceholder")}
              onChange={(value) => schedule.onEdit("duration_days", value)}
            />
          </Form.Item>
        </Tooltip>
      </td>
    ),
    // Actual End
    actual_end_date: (
      <Tooltip
        title={
          completedWithoutActualEnd
            ? t("projectMilestones.tooltips.completedNeedsActualEnd")
            : ""
        }
        color="orange"
      >
        <td className="ant-table-cell">
          <Form.Item<ProjectMilestone>
            name="actual_end_date"
            noStyle
            getValueProps={getDateValueProps}
          >
            <DatePicker
              format={pickerFormat}
              status={completedWithoutActualEnd ? "warning" : ""}
              onChange={onActualEndChangeHandler}
              suffixIcon={
                isLate ? (
                  <WarningFilled className="text-warning" />
                ) : (
                  <CalendarOutlined />
                )
              }
            />
          </Form.Item>
        </td>
      </Tooltip>
    ),
    // Variance
    variance: (
      <td className="ant-table-cell text-center">
        {variance === null ? empty : variance}
      </td>
    ),
    // Dependencies
    dependencies: (
      <td className="ant-table-cell text-center">
        <Tooltip title={t("projectMilestones.tooltips.viewModeOnly")}>
          <Button disabled icon={<LinkOutlined />} />
        </Tooltip>
      </td>
    ),
    // Evidence
    attachments: (
      <td className="ant-table-cell text-center">
        <Tooltip title={t("projectMilestones.tooltips.viewModeOnly")}>
          <Button disabled icon={<PaperClipOutlined />} />
        </Tooltip>
      </td>
    )
  }

  const actionsCell = (
    <td
      className={`sticky right-0 ant-table-cell ant-table-cell-fix-right ant-table-cell-fix-right-first ${rowClassName}`}
    >
      <Flex gap="small" justify="center">
        <Button
          variant="outlined"
          color="danger"
          onClick={handleCancel}
          icon={<CloseOutlined />}
        />
        <Button
          loading={saving}
          variant="outlined"
          color="green"
          onClick={handleSave}
          icon={mode === "create" ? <PlusOutlined /> : <CheckOutlined />}
        />
      </Flex>
    </td>
  )

  return (
    <Form
      form={form}
      component={false}
      initialValues={initialValues}
      disabled={saving}
      validateTrigger={["onSubmit", "onChange"]}
    >
      <FormChangedWatcher<ProjectMilestone> form={form} saving={saving} />

      <tr
        {...rest}
        ref={rowRef}
        onKeyDown={onKeyDownRow}
        className={rowClassName}
      >
        {/* Counting these instead goes wrong the moment the table gains or
            loses a leading column — that is how every field from Milestone Name
            on ended up a column to the left. Wrapping them would nest a <td>
            inside a <td>, out of reach of any stylesheet. */}
        {byColumn
          ? buildEditRowCells(
              tableCells,
              // "#" can be moved too; it has no editor, so it stays as it is.
              [...MILESTONE_EDITABLE_COLUMN_KEYS, ORDER_COLUMN_KEY],
              editors,
              actionsCell
            )
          : [
              ...leadingCells,
              ...Object.entries(editors).map(([key, editor]) =>
                React.cloneElement(editor, { key })
              ),
              React.cloneElement(actionsCell, { key: "actions" })
            ]}
      </tr>
    </Form>
  )
}
